import { useCallback, useState, useRef, useEffect } from 'react';
import { useAuth } from 'src/auth/useAuth';
import { AuthLogo } from 'src/components/auth/AuthLogo';
import { clearEntraTokenCache } from 'src/services/utils/authTokenHttp';
import {
  captureSessionException,
  captureSessionMessage,
} from 'src/utils/sentry/sessionSentry';

export interface SessionExpiredOverlayProps {
  /** Whether the overlay is visible */
  readonly isVisible: boolean;
  /** Optional callback when user clicks "Log In Again" */
  readonly onLoginAgain?: () => void;
  /** Optional callback to reset parent component state after successful token refresh */
  readonly onTokenRefreshSuccess?: () => void;
  /** Optional custom message to display */
  readonly customMessage?: string;
  /** Whether to show in testing mode */
  readonly testingMode?: boolean;
}

/**
 * SessionExpiredOverlay Component
 *
 * Full-screen overlay shown when the user session has expired.
 * "Log In Again" triggers auth logout (redirect to login page).
 * Styled to match the AuthLayout / CustomLoginPage theme.
 */
export function SessionExpiredOverlay({
  isVisible,
  onLoginAgain,
  onTokenRefreshSuccess,
  customMessage,
  testingMode = false,
}: SessionExpiredOverlayProps) {
  const auth = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const isLoggingOutRef = useRef(false);

  // Focus management
  useEffect(() => {
    if (isVisible && dialogRef.current) {
      dialogRef.current.focus();
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isVisible]);

  /**
   * Log In Again: clear caches, dismiss overlay, and log out (redirects to login).
   */
  const handleLoginAgain = useCallback(async () => {
    if (isLoggingOutRef.current) return;

    try {
      isLoggingOutRef.current = true;
      setIsLoggingOut(true);

      captureSessionMessage(
        'Session expired overlay: login again clicked',
        'warning',
        {
          event: 'session.expired.overlay.loginAgain.clicked',
        },
      );

      clearEntraTokenCache();
      onTokenRefreshSuccess?.();
      onLoginAgain?.();

      await auth.actions.logout();
    } catch (error) {
      console.error('SessionExpiredOverlay: Logout failed:', error);
      captureSessionException(error, {
        event: 'session.expired.overlay.logoutFailed',
      });
      // Even if logout fails, try to redirect
      globalThis.location?.assign(globalThis.location?.origin ?? '/');
    } finally {
      isLoggingOutRef.current = false;
      setIsLoggingOut(false);
    }
  }, [auth.actions, onLoginAgain, onTokenRefreshSuccess]);

  if (!isVisible) {
    return null;
  }

  return (
    <dialog
      ref={dialogRef}
      open={true}
      className="fixed inset-0 z-50 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center border-0 p-4 m-0 min-w-full min-h-full"
      aria-labelledby="session-expired-title"
      aria-describedby="session-expired-description"
      aria-modal="true"
      data-inactivity-warning="true"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden flex flex-col p-8 relative">
        <div className="flex flex-col items-center text-center">
          {/* Logo */}
          <AuthLogo size="lg" className="mb-6" />

          {/* Icon */}
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-amber-50 mb-5 relative">
            <svg
              className="h-8 w-8 text-amber-500"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
              />
            </svg>
            <div className="absolute inset-0 rounded-full border border-amber-500/20 animate-ping"></div>
          </div>

          {/* Title */}
          <h1
            id="session-expired-title"
            className="text-2xl font-bold text-gray-900 mb-2"
          >
            Session Expired
          </h1>

          {/* Description */}
          <p
            id="session-expired-description"
            className="text-sm text-gray-500 mb-8 leading-relaxed max-w-sm"
          >
            {isLoggingOut
              ? 'Redirecting to sign-in...'
              : (customMessage ??
                'Your session has expired due to inactivity. Please sign in again to continue.')}
          </p>

          {/* Testing Mode Indicator */}
          {testingMode && (
            <div className="mb-6 w-full p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-700 font-medium tracking-tight">
                Testing Mode: Session expiration simulation
              </p>
            </div>
          )}

          {/* Log In Again Button */}
          <button
            type="button"
            onClick={handleLoginAgain}
            disabled={isLoggingOut}
            className={`w-full max-w-xs flex justify-center items-center gap-2 py-3 px-6 rounded-xl text-sm font-semibold tracking-wide transition-all duration-200 ${
              isLoggingOut
                ? 'bg-blue-400 text-white cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600 active:scale-[0.98]'
            }`}
          >
            {isLoggingOut && (
              <svg
                className="animate-spin h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {isLoggingOut ? 'Signing out...' : 'Sign In Again'}
          </button>
        </div>
      </div>
    </dialog>
  );
}

export default SessionExpiredOverlay;
